import { useCallback, useContext, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import FormElement from "rc-field-form";
import moment from "moment-timezone";
import { Card } from "reactstrap";
import Button from "../../../../components/Button";
import Loading from "../../../../components/Loaders/Loading";
import WeekdaysCard from "./WeekdaysCard";
import { useApiAvailability } from "../api";
import { getWeekdays, hasDayChanges, selectDayEntries, serializeDay, toFormDay, toPayloadDay } from "../helpers";
import { useCompanyTimeZone } from "../../../../utils/hooks/company";
import { ProfileContext } from "../../context";
import { useAccess } from "../../../../utils/hooks/access";
import "./style.scss";

const Reoccurring = () => {
    const { t } = useTranslation();
    const [form] = FormElement.useForm();
    const { create, data, getAvailability, loading, submitting } = useApiAvailability();
    const timezone = useCompanyTimeZone();
    const { isSelf, user } = useContext(ProfileContext);
    const { hasAccess: canManageUsers } = useAccess("canManageUsers");

    const weekdays = useMemo(() => getWeekdays(), []);

    const url = isSelf ? "/profile/availability" : `/users/${user?.id}/availability`;

    const fetch = useCallback((controller) => {
        if (isSelf || user?.id) {
            getAvailability(url, { type: "recurring" }, controller);
        }
    }, [getAvailability, url, isSelf, user?.id]);

    useEffect(() => {
        const controller = new AbortController();
        fetch(controller);
        return () => controller.abort();
    }, [fetch]);

    // The form edits the pending request of each day; approved records are shown read-only.
    const initialValues = useMemo(() => {
        const pending = data?.filter?.((item) => item.status === "pending");
        return weekdays.reduce((total, day) => ({
            ...total,
            [day]: toFormDay(selectDayEntries(pending, day).formEntry, {
                date: moment(),
                timezone,
                withDates: true,
            }),
        }), {});
    }, [data, weekdays, timezone]);

    useEffect(() => {
        form.setFieldsValue(initialValues);
    }, [form, initialValues]);

    const formValues = FormElement.useWatch(undefined, form);

    const hasChanges = useMemo(
        () => (formValues ? hasDayChanges(formValues, initialValues, weekdays) : false),
        [formValues, initialValues, weekdays]
    );

    const onReset = useCallback(() => {
        form.resetFields();
        form.setFieldsValue(initialValues);
    }, [form, initialValues]);

    const onFinish = useCallback(
        (values) => {
            const date = moment().format("YYYY-MM-DD");
            const days = weekdays.reduce((total, day) => {
                const dayData = values?.[day];

                // Only submit days whose values differ from what the form was loaded with.
                if (!dayData || serializeDay(dayData) === serializeDay(initialValues[day])) {
                    return total;
                }

                const payloadDay = toPayloadDay(day, dayData, { type: "recurring", date, timezone });
                return payloadDay ? [...total, payloadDay] : total;
            }, []);

            create(days, () => fetch());
        },
        [create, fetch, initialValues, timezone, weekdays]
    );

    return (
        <FormElement form={form} onFinish={onFinish} className="h-100">
            {loading && !data ? (
                <div className="d-flex justify-content-center">
                    <Loading />
                </div>
            ) : (
                <Card className="days-list-height shadow-none">
                    {weekdays.map((day) => {
                        const { formEntry, approved, history } = selectDayEntries(data, day);
                        return (
                            <WeekdaysCard
                                key={day}
                                day={day}
                                data={formEntry}
                                approved={approved}
                                history={history}
                                initialValue={initialValues[day]}
                                form={form}
                                disabled={!isSelf && canManageUsers}
                            />
                        );
                    })}
                </Card>
            )}
            {isSelf && (
                <div className="d-flex mt-2 justify-content-end gap-2">
                    <Button type="button" onClick={onReset} color="muted" disabled={!hasChanges || loading}>
                        {t("reset")}
                    </Button>
                    <Button type="submit" color="primary" disabled={!hasChanges || loading} loading={submitting}>
                        {t("save")}
                    </Button>
                </div>
            )}
        </FormElement>
    );
};

export default Reoccurring;
